import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Box, CircularProgress } from '@mui/material'
import ClientesHead from '../components/ClientesHead'
import ClientesTable from '../components/ClientesTable'
import ClienteDialog from '../../Vendedor/components/ClienteDialog'
import SeguimientoContainer from '../../Vendedor/containers/SeguimientoContainer'
import { apiFetch } from '../../api'
import socket from '../../socket'
import { getUserContext } from '../../auth'

export default function ClientesContainer({
  onBackHome,
  onOpenNoAsignadas,
  onOpenCerradas,
  isFullScreen,
  onToggleFullScreen,
  initialOpenClienteId,
  onInitialOpenConsumed,
}) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [limit, setLimit] = useState(20)

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [estado, setEstado] = useState('TODOS')
  const [orderBy, setOrderBy] = useState('fecha_carga')
  const [orderDir, setOrderDir] = useState('desc')
  const [showArchived, setShowArchived] = useState(false)
  const [archivedTotal, setArchivedTotal] = useState(0)
  const [archiveUpdatingId, setArchiveUpdatingId] = useState(null)

  const [openClient, setOpenClient] = useState(false)
  const [clientData, setClientData] = useState(null)
  const [clientLoading, setClientLoading] = useState(false)
  const [clientSaving, setClientSaving] = useState(false)
  const [openSeg, setOpenSeg] = useState(false)
  const [segAsign, setSegAsign] = useState(null)

  useEffect(() => {
    setLimit(isFullScreen ? 100 : 20)
    setPage(0)
  }, [isFullScreen])

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(0)
    }, 280)
    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    setPage(0)
  }, [estado, orderBy, orderDir, showArchived])

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(limit),
        estado_actual: estado,
        order_by: orderBy,
        order_dir: orderDir,
        archivado: String(showArchived),
      })
      if (search) params.set('search', search)

      const res = await apiFetch(`/api/clientes/listado?${params.toString()}`, {
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      setRows(Array.isArray(data.items) ? data.items : [])
      setTotal(data.total || 0)
    } catch (e) {
      console.error('Error cargando clientes:', e)
      setRows([])
      setTotal(0)
      setError('No se pudo cargar la lista de clientes.')
    } finally {
      setLoading(false)
    }
  }, [page, limit, search, estado, orderBy, orderDir, showArchived])

  const fetchArchivedTotal = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '1',
        estado_actual: 'TODOS',
        order_by: 'fecha_carga',
        order_dir: 'desc',
        archivado: 'true',
      })
      const res = await apiFetch(`/api/clientes/listado?${params.toString()}`, {
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setArchivedTotal(Number(data.total || 0))
    } catch (e) {
      console.error('Error cargando total de clientes archivados:', e)
      setArchivedTotal(0)
    }
  }, [])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  useEffect(() => {
    fetchArchivedTotal()
  }, [fetchArchivedTotal])

  useEffect(() => {
    if (!socket) return undefined
    let timer = null
    const refresh = (data) => {
      const user = getUserContext()
      if (user && data?.empresa_id && String(data.empresa_id) !== String(user.empresa_id)) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        fetchRows()
        fetchArchivedTotal()
      }, 220)
    }

    socket.on('create_cliente', refresh)
    socket.on('update_cliente', refresh)
    socket.on('delete_cliente', refresh)
    socket.on('create_asignacion', refresh)
    socket.on('update_estado_lead', refresh)
    socket.on('create_devolucion', refresh)
    return () => {
      socket.off('create_cliente', refresh)
      socket.off('update_cliente', refresh)
      socket.off('delete_cliente', refresh)
      socket.off('create_asignacion', refresh)
      socket.off('update_estado_lead', refresh)
      socket.off('create_devolucion', refresh)
      if (timer) clearTimeout(timer)
    }
  }, [fetchRows, fetchArchivedTotal])

  const handleOpenCliente = async (row) => {
    const clienteId = row?.id_cliente ?? row?.cliente_id ?? row?.id
    if (!clienteId) return
    setOpenClient(true)
    setClientData(null)
    setClientLoading(true)
    try {
      const res = await apiFetch(`/api/clientes/${clienteId}`, { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setClientData(data)
    } catch (e) {
      console.error('Error cargando cliente:', e)
      setOpenClient(false)
      setError('No se pudo abrir el cliente.')
    } finally {
      setClientLoading(false)
    }
  }

  useEffect(() => {
    if (!initialOpenClienteId) return
    handleOpenCliente({ id: initialOpenClienteId })
    onInitialOpenConsumed?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpenClienteId])

  const handleChangeClient = (next) => {
    setClientData(next)
    fetchRows()
  }

  const handleSaveClient = async (cliente) => {
    if (!cliente?.id) return null
    setClientSaving(true)
    try {
      const payload = {
        nombre: cliente.nombre,
        telefono: cliente.telefono || null,
        email: cliente.email || null,
        obtenido_de: cliente.obtenido_de || null,
        observacion: cliente.observacion || null,
      }

      const res = await apiFetch(`/api/clientes/${cliente.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || `HTTP ${res.status}`)
      }
      const updated = await res.json()
      setClientData(updated)
      await fetchRows()
      return updated
    } catch (e) {
      console.error('Error guardando cliente:', e)
      throw e
    } finally {
      setClientSaving(false)
    }
  }

  const handleOpenSeguimiento = (asignacion) => {
    setSegAsign(asignacion)
    setOpenSeg(true)
  }

  const handleArchiveChange = async (row, archivado) => {
    if (!row?.id || archiveUpdatingId) return
    setArchiveUpdatingId(row.id)
    setError(null)
    try {
      const res = await apiFetch(`/api/clientes/${row.id}/archivado`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ archivado }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || `HTTP ${res.status}`)
      }
      await fetchArchivedTotal()
      await fetchRows()
    } catch (e) {
      console.error('Error actualizando archivado:', e)
      setError(e.message || 'No se pudo actualizar el estado de archivado.')
    } finally {
      setArchiveUpdatingId(null)
    }
  }

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
      px: isFullScreen ? 2 : 5,
      pt: isFullScreen ? 3 : 4,
    }}>
      <ClientesHead
        search={searchInput}
        onSearchChange={setSearchInput}
        estado={estado}
        onEstadoChange={setEstado}
        orderBy={orderBy}
        onOrderByChange={setOrderBy}
        orderDir={orderDir}
        onOrderDirChange={setOrderDir}
        showArchived={showArchived}
        archivedTotal={archivedTotal}
        onToggleArchived={() => setShowArchived((prev) => !prev)}
        onBackHome={onBackHome}
        onOpenNoAsignadas={onOpenNoAsignadas}
        onOpenCerradas={onOpenCerradas}
        isFullScreen={isFullScreen}
        onToggleFullScreen={onToggleFullScreen}
      />

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <ClientesTable
          rows={rows}
          count={total}
          page={page}
          rowsPerPage={limit}
          onPageChange={(event, newPage) => setPage(newPage)}
          onOpenCliente={handleOpenCliente}
          onArchiveChange={handleArchiveChange}
          archiveUpdatingId={archiveUpdatingId}
          customHeight="flex"
        />
        {loading && (
          <Box sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            backgroundColor: 'rgba(255,255,255,0.36)',
          }}>
            <CircularProgress size={30} />
          </Box>
        )}
      </Box>

      <ClienteDialog
        open={openClient}
        onClose={() => setOpenClient(false)}
        cliente={clientData}
        onChange={handleChangeClient}
        onSave={handleSaveClient}
        onOpenSeguimiento={handleOpenSeguimiento}
        saving={clientSaving}
        loading={clientLoading}
      />

      <SeguimientoContainer
        open={Boolean(openSeg && segAsign)}
        onClose={() => { setOpenSeg(false); setSegAsign(null) }}
        asignacion={segAsign ?? null}
        adminReadOnly={true}
      />
    </Box>
  )
}
